// Bookie Emerald (Zumrad) — preview thumbnail. The full design lives
// at /templates/bookie-emerald.html (Bookie-formula wedding card on a
// cream paper base with emerald #2C5F4F as the single accent, gold
// #B89968 highlights, Cormorant Garamond display + body in weight
// variation, and Allura as the lone script flourish). Motif: olive
// branch arch above each section eyebrow + a single olive sprig in
// the HERO's bottom-left and bottom-right corners. The catalog card
// is a compact 5:7 echo of that hero — olive arch up top, an evergreen
// eyebrow, the couple's names with an Allura ampersand, and a thin
// gold rule above the date/venue footer.
//
// Tone: classical, Mediterranean, formal-evergreen.
//
// The iframe used by the live variant is in
// /components/variants/bookie-emerald-static.tsx; this file is the
// thumbnail React preview shown in the catalog grid.

import type { LayoutCtx } from "./_shared";

export function BookieEmeraldPreviewLayout({ names, date, venue, ink, bg }: LayoutCtx) {
  const [a, b] = names.split(/\s*&\s*/);
  // Lock the theme tones at the preview level — palette is fixed for
  // this template (the catalog row's accent prop is intentionally
  // ignored so the thumbnail always reads "evergreen + gold").
  const emerald = "#2C5F4F";
  const gold = "#B89968";
  const paper = "#F6EFE0";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, #FBF7EC 0%, ${paper} 28%, ${bg} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "13% 9% 9%",
      }}
    >
      {/* Olive-branch arch — the signature motif. */}
      <svg
        aria-hidden
        viewBox="0 0 200 56"
        style={{ width: "62%", height: "auto", color: emerald, opacity: 0.95 }}
      >
        <path
          d="M16 50 Q 100 4 184 50"
          stroke="currentColor"
          strokeWidth="0.9"
          fill="none"
          opacity={0.78}
        />
        <g fill="currentColor" opacity={0.72}>
          <ellipse cx="92"  cy="9"  rx="7"   ry="2.4" transform="rotate(-18 92 9)" />
          <ellipse cx="78"  cy="13" rx="7.5" ry="2.5" transform="rotate(-30 78 13)" />
          <ellipse cx="64"  cy="19" rx="8"   ry="2.6" transform="rotate(-40 64 19)" />
          <ellipse cx="50"  cy="27" rx="8"   ry="2.6" transform="rotate(-50 50 27)" />
          <ellipse cx="38"  cy="37" rx="7"   ry="2.4" transform="rotate(-58 38 37)" />
          <ellipse cx="108" cy="9"  rx="7"   ry="2.4" transform="rotate(18 108 9)" />
          <ellipse cx="122" cy="13" rx="7.5" ry="2.5" transform="rotate(30 122 13)" />
          <ellipse cx="136" cy="19" rx="8"   ry="2.6" transform="rotate(40 136 19)" />
          <ellipse cx="150" cy="27" rx="8"   ry="2.6" transform="rotate(50 150 27)" />
          <ellipse cx="162" cy="37" rx="7"   ry="2.4" transform="rotate(58 162 37)" />
        </g>
        <ellipse cx="96"  cy="6" rx="2.1" ry="2.7" fill={gold} opacity={0.95} />
        <ellipse cx="104" cy="6" rx="2.1" ry="2.7" fill={gold} opacity={0.95} />
        <ellipse cx="100" cy="3" rx="2"   ry="2.5" fill={gold} opacity={0.85} />
      </svg>

      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          color: emerald,
          marginTop: 10,
        }}
      >
        SAVE THE DATE
      </div>

      {/* Couple names — Cormorant Garamond. */}
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 400,
          fontSize: 18,
          lineHeight: 1.05,
          color: ink,
          marginTop: 10,
        }}
      >
        {a ?? names}
      </div>
      <div
        style={{
          fontFamily: "'Allura', cursive",
          fontSize: 22,
          color: gold,
          lineHeight: 0.8,
          margin: "2px 0",
        }}
      >
        &amp;
      </div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 400,
          fontSize: 18,
          color: ink,
          lineHeight: 1.05,
        }}
      >
        {b ?? ""}
      </div>

      {/* Allura tagline — the single script accent. */}
      <div
        style={{
          fontFamily: "'Allura', cursive",
          fontSize: 14,
          color: emerald,
          lineHeight: 1.2,
          marginTop: 8,
        }}
      >
        are getting married
      </div>

      {/* Thin gold rule above the meta footer. */}
      <div
        aria-hidden
        style={{
          width: 28,
          height: 1,
          background: gold,
          opacity: 0.85,
          margin: "12px 0 8px",
        }}
      />

      {/* Date — tabular numerals for clean alignment. */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 8,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: ink,
          opacity: 0.78,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontStyle: "italic",
          fontSize: 10,
          color: ink,
          opacity: 0.7,
          marginTop: 2,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
